import Consts from '@/api/Consts';
import MapHelpers from '@/components/map/utils/MapHelpers';


export type NearElevationItem = {
	current_datetime: string;
	mountpoint: string;
	gnss: string;
	band: string;
	kind: 'visible' | 'signal';
	elevations: number[];
};

export type HeatmapTooltipRow = {
	label: string;
	value: string;
};

export type HeatmapCellClassification = {
	status: number | null;
	tooltipRows: HeatmapTooltipRow[];
};

export type HeatmapChartPointItem = {
	current_datetime: string;
	mountpoint: string;
	status: number | null;
	tooltipRows: HeatmapTooltipRow[];
};

export const HEATMAP_STATUS_OK = 0;
export const HEATMAP_STATUS_BREAK = 25;
export const HEATMAP_STATUS_SINGLE = 50;
export const HEATMAP_STATUS_MULTI = 100;
export const HEATMAP_OK_COLOR = '9bc4b0';
export const HEATMAP_BREAK_COLOR = '8ea6c6';
export const HEATMAP_MULTI_COLOR = 'eb0f90';
// BDS chart yellow (d8cf26) washes out on grey heatmap rows; keep global GNSS color unchanged.
export const HEATMAP_GNSS_COLOR_OVERRIDES: Readonly<Record<string, string>> = {
	C: 'c9b000',
};
// Flip to true to restore per-GNSS cell colors (GPS/GLO/GAL/BDS) instead of unified yellow.
export const USE_PER_GNSS_HEATMAP_COLORS = false;
export const HEATMAP_SINGLE_COLOR = 'c9b000';

export const SPOOFING_GNSS_CODES = Consts.sortGnssCodes(
	Consts.GNSS_DATA.filter(item => item.isActual).map(item => item.code),
);

const GNSS_STATUS_CODES: Readonly<Record<string, number>> = {
	G: 1,
	R: 2,
	E: 3,
	C: 4,
};


export const countAtOrAbove = (elevations: number[], cutoffDeg: number): number => {
	return elevations.filter(elevation => elevation >= cutoffDeg).length;
};


export const signalPercent = (signalCount: number, visibleCount: number): number => {
	if (visibleCount <= 0) {
		return 0;
	}
	return Math.round((signalCount * 100) / visibleCount);
};


export const getGnssFailStatusCode = (gnss: string): number => {
	const code = GNSS_STATUS_CODES[gnss];
	if (code == null) {
		return HEATMAP_STATUS_MULTI;
	}
	return code;
};


export const getGnssCodeForStatus = (status: number): string | null => {
	for (const [gnss, code] of Object.entries(GNSS_STATUS_CODES)) {
		if (code === status) {
			return gnss;
		}
	}
	return null;
};


type BucketIndex = {
	visible: Map<string, number[]>;
	signal: Map<string, number[]>;
	gnssCodes: Set<string>;
};


const visibleKey = (gnss: string): string => gnss;


const signalKey = (gnss: string, band: string): string => gnss + '|' + band;


export const indexNearElevationBucket = (items: NearElevationItem[]): BucketIndex => {
	const visible = new Map<string, number[]>();
	const signal = new Map<string, number[]>();
	const gnssCodes = new Set<string>();
	for (const item of items) {
		gnssCodes.add(item.gnss);
		if (item.kind === 'visible') {
			visible.set(visibleKey(item.gnss), item.elevations || []);
		} else {
			signal.set(signalKey(item.gnss, item.band), item.elevations || []);
		}
	}
	return { visible, signal, gnssCodes };
};


export const classifyHeatmapCell = (
	items: NearElevationItem[],
	bands: string[],
	cutoffDeg: number,
	visibilityThreshold: number,
	perGnssColors: boolean = USE_PER_GNSS_HEATMAP_COLORS,
): HeatmapCellClassification => {
	if (items.length === 0) {
		return { status: null, tooltipRows: [] };
	}
	const index = indexNearElevationBucket(items);
	const failingGnss: string[] = [];
	const tooltipRows: HeatmapTooltipRow[] = [];
	const sortedGnss = Consts.sortGnssCodes(
		Array.from(index.gnssCodes).filter(gnss => SPOOFING_GNSS_CODES.includes(gnss)),
	);
	for (const gnss of sortedGnss) {
		const possibleBands: readonly string[] = Consts.GNSS_POSSIBLE_BANDS[gnss as keyof typeof Consts.GNSS_POSSIBLE_BANDS] || [];
		const bandsToCheck = bands.filter(band => possibleBands.includes(band));
		if (bandsToCheck.length === 0) {
			continue;
		}
		const visibleElevations = index.visible.get(visibleKey(gnss)) || [];
		const visCount = countAtOrAbove(visibleElevations, cutoffDeg);
		let gnssFailed = false;
		let minPercent: number | null = null;
		for (const band of bandsToCheck) {
			const signalElevations = index.signal.get(signalKey(gnss, band)) || [];
			const sigCount = countAtOrAbove(signalElevations, cutoffDeg);
			const percent = signalPercent(sigCount, visCount);
			if (minPercent == null || percent < minPercent) {
				minPercent = percent;
			}
			if (percent < visibilityThreshold) {
				gnssFailed = true;
			}
		}
		const gnssName = MapHelpers.getGnssDataDisplay(gnss).name;
		tooltipRows.push({
			label: gnssName,
			value: String(minPercent == null ? 0 : minPercent) + '%',
		});
		if (gnssFailed) {
			failingGnss.push(gnss);
		}
	}
	if (failingGnss.length === 0) {
		return { status: HEATMAP_STATUS_OK, tooltipRows };
	}
	if (failingGnss.length === 1) {
		const onlyFailing = failingGnss[0];
		if (onlyFailing == null) {
			return { status: HEATMAP_STATUS_MULTI, tooltipRows };
		}
		if (perGnssColors) {
			return { status: getGnssFailStatusCode(onlyFailing), tooltipRows };
		}
		return { status: HEATMAP_STATUS_SINGLE, tooltipRows };
	}
	return { status: HEATMAP_STATUS_MULTI, tooltipRows };
};


export const buildHeatmapChartItems = (
	items: NearElevationItem[],
	bands: string[],
	cutoffDeg: number,
	visibilityThreshold: number,
	perGnssColors: boolean = USE_PER_GNSS_HEATMAP_COLORS,
): HeatmapChartPointItem[] => {
	const byCell = new Map<string, NearElevationItem[]>();
	for (const item of items) {
		const key = item.mountpoint + '|' + item.current_datetime;
		const existing = byCell.get(key);
		if (existing == null) {
			byCell.set(key, [item]);
		} else {
			existing.push(item);
		}
	}
	const result: HeatmapChartPointItem[] = [];
	for (const cellItems of byCell.values()) {
		const first = cellItems[0];
		if (first == null) {
			continue;
		}
		const classification = classifyHeatmapCell(
			cellItems,
			bands,
			cutoffDeg,
			visibilityThreshold,
			perGnssColors,
		);
		result.push({
			current_datetime: first.current_datetime,
			mountpoint: first.mountpoint,
			status: classification.status,
			tooltipRows: classification.tooltipRows,
		});
	}
	return result;
};


export const buildHeatmapColorSettings = (
	okLabel: string,
	multiLabel: string,
	gnssIssueLabels: Readonly<Record<string, string>>,
	singleLabel: string,
	perGnssColors: boolean = USE_PER_GNSS_HEATMAP_COLORS,
	breakLabel?: string | null,
): Array<{ exactValue: number; color: string; label: string }> => {
	const settings: Array<{ exactValue: number; color: string; label: string }> = [
		{ exactValue: HEATMAP_STATUS_OK, color: HEATMAP_OK_COLOR, label: okLabel },
	];
	if (breakLabel != null && breakLabel !== '') {
		settings.push({
			exactValue: HEATMAP_STATUS_BREAK,
			color: HEATMAP_BREAK_COLOR,
			label: breakLabel,
		});
	}
	if (perGnssColors) {
		for (const gnssCode of SPOOFING_GNSS_CODES) {
			const status = GNSS_STATUS_CODES[gnssCode];
			if (status == null) {
				continue;
			}
			const gnssItem = MapHelpers.getGnssDataDisplay(gnssCode);
			settings.push({
				exactValue: status,
				color: HEATMAP_GNSS_COLOR_OVERRIDES[gnssCode] || gnssItem.color,
				label: gnssIssueLabels[gnssCode] || (gnssItem.name + ' issue'),
			});
		}
	} else {
		settings.push({
			exactValue: HEATMAP_STATUS_SINGLE,
			color: HEATMAP_SINGLE_COLOR,
			label: singleLabel,
		});
	}
	settings.push({
		exactValue: HEATMAP_STATUS_MULTI,
		color: HEATMAP_MULTI_COLOR,
		label: multiLabel,
	});
	return settings;
};
